import { Component, OnDestroy, OnInit } from '@angular/core';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { InvoicesService } from 'src/app/api/services';
import { InvoiceCalculatorService, InvoicesAndSummary } from 'src/app/service/invoice-calculator/invoice-calculator.service';
import { Money } from 'src/app/model/money';
import { Invoice } from 'src/app/api/models';
import { MoneyPipe } from 'src/app/pipe/money.pipe';
import { InvoiceNotificationsService } from 'src/app/service/invoice-notifications.service';
import { EndCustomerPeerService } from 'src/app/service/end-customer-peer.service';
import { InvoicePaymentStatus } from '../../model/invoice';

@Component({
  selector: 'app-invoice-end-customer-invoices-widget',
  templateUrl: './invoice-end-customer-invoices-widget.component.html',
  styleUrls: ['./invoice-end-customer-invoices-widget.component.scss']
})
export class InvoiceEndCustomerInvoicesWidgetComponent implements OnInit, OnDestroy {
  private readonly ngDestroy$ = new Subject<void>();

  data$: Observable<InvoicesAndSummary>;
  creditLimitLeft$: Observable<Money>;

  subtitle: string;

  showInvoices = false;

  constructor(
    private invoicePeerService: InvoicePeerService,
    private endCustomerPeerService: EndCustomerPeerService,
    private invoicesService: InvoicesService,
    private moneyPipe: MoneyPipe,
    private invoiceCalculatorService: InvoiceCalculatorService,
    private invoiceNotificationsService: InvoiceNotificationsService,
    private router: Router
  ) {
  }

  toggleInvoices(): void {
    this.showInvoices = !this.showInvoices;
  }

  showInvoice(invoiceId: number): void {
    this.router.navigateByUrl('invoices/' + invoiceId);
  }

  getPaymentStatus(invoice: Invoice): InvoicePaymentStatus {
    return this.invoiceCalculatorService.getPaymentStatus(invoice);
  }

  getTotalOpen(invoice: Invoice): Money {
    const m = new Money(this.invoiceCalculatorService.getTotalOpen(invoice));
    if (m.toDinero().getAmount() === 0) {
      return null;
    }
    return m;
  }

  ngOnInit() {
    this.data$ = this.invoicePeerService
      .getInvoice()
      .pipe(
        switchMap((invoice: Invoice) => {
          // AI: An invoice may not have an end customer yet.
          if (invoice && invoice.end_customer_id != null) {
            return this.invoicesService.getEndCustomerInvoices({ end_customer_id: invoice.end_customer_id })
              .pipe(
                map(page => {
                  return page.items;
                }),
                shareReplay(1));
          } else {
            return of([]);
          }
        }),
        map((invoices: Invoice[]) => {
          const d = this.invoiceCalculatorService.getInvoicesAndSummary(invoices);

          this.subtitle = this.moneyPipe.transform(d.open.amount, 'symbol', '1.2-2');
          if (d.overdue.count > 0) {
            this.subtitle += "⚠️";
            this.invoiceNotificationsService.setEndCustomerOverdue(true);
          }

          return d;
        })
      );

    this.creditLimitLeft$ = combineLatest([this.data$, this.endCustomerPeerService.get()])
      .pipe(
        map(([data, endCustomer]) => {
          if (!endCustomer?.credit_limit?.limit?.amount || !endCustomer.credit_limit.limit.currency) {
            return new Money(null);
          }
          const limit = new Money(endCustomer.credit_limit.limit);
          const left = new Money(limit.toDinero().subtract(data.open.amount.toDinero()));
          if (left.toDinero().isNegative()) {
            this.invoiceNotificationsService.setEndCustomerOverCreditLimit(true);
          }

          return left;
        })
      );
  }

  ngOnDestroy() {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }
}
